/**
 * PROPERTY <entity-id> <name> <value>
 *
 * Unified single-entity property-editing command. Routes the value to
 * either `dynamic/set` (if `name` matches a customProperty of a dynamic
 * block INSERT) or `chprop/apply` with a single-entity batch (if `name`
 * is a standard entity property — Layer / Color / Linetype / Linetype
 * scale / Lineweight / Transparency).
 *
 * AutoCAD does not have a literal "PROPERTY" command-line entry — power
 * users reach for the Properties palette (Ctrl+1). We expose this as a
 * scriptable unified command so AI agents can change any property on a
 * single entity without branching on dynamic-block-ness.
 *
 * Pure function: no I/O, no reducer coupling, no document mutation. The
 * caller dispatches through the existing `dynamic/set` or `chprop/apply`
 * reducers so the audit log, history, selection-preservation and shell
 * re-sync machinery stay unchanged.
 *
 * Routing precedence (first match wins):
 *   1. Entity has a `customProperties[name]` → `dynamic-param`
 *      (dynamic-block INSERT parameter takes priority even if the name
 *      happens to match a StandardProperty, because the INSERT's own
 *      bag is the source of truth when it exists.)
 *   2. `name` case-insensitively matches a StandardProperty →
 *      `entity-property` with the canonical-cased name.
 *   3. Else → `{kind:"error", code:"unknown-property"}`.
 *
 * Validation:
 *   - `entityId` must resolve through the caller-supplied lookup.
 *   - `name` after .trim() must be non-empty.
 *   - `rawValue` is passed through as-is; downstream reducers handle
 *     type coercion. This mirrors DYNSET's contract.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Public types
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Canonical AutoCAD names of the "common object properties" every
 * entity surfaces regardless of type. The set matches the
 * `commonObjectProperties` tuple in `propertyCapabilities.ts` that CHPROP
 * already consumes — we keep it in sync deliberately so the route into
 * `chprop/apply` with one of these names always lands on a valid
 * property. Plot Style / Thickness / Material / Annotative are
 * deliberately excluded here because the task scope calls out Layer /
 * Color / Linetype / Linetype scale / Lineweight / Transparency as
 * supported. Extending later is a matter of appending to this tuple and
 * its display-name map.
 */
export const STANDARD_PROPERTIES = [
  "Layer",
  "Color",
  "Linetype",
  "Linetype scale",
  "Lineweight",
  "Transparency",
] as const;

export type StandardProperty = (typeof STANDARD_PROPERTIES)[number];

/**
 * Canonical property names for propertyCapabilities / propertyReducer.
 * The store expects Title-Case names ("Linetype Scale") as keys in
 * EntityPreview.properties; we store "Linetype scale" in the
 * STANDARD_PROPERTIES tuple (sentence case matches the ТЗ spec), so
 * when dispatching to chprop/apply we map through this table to hit the
 * exact store key.
 */
const STANDARD_PROPERTY_STORE_KEYS: Record<StandardProperty, string> = {
  Layer: "Layer",
  Color: "Color",
  Linetype: "Linetype",
  "Linetype scale": "Linetype Scale",
  Lineweight: "Lineweight",
  Transparency: "Transparency",
};

export type PropertyTarget = {
  entityId: string;
  propertyName: string;
  rawValue: string;
};

/**
 * Minimal lookup the caller must provide — the command engine stays
 * document-agnostic. `findEntity` returns the subset of the entity we
 * need (id + optional customProperties bag). Returning `undefined`
 * signals "entity not present in the active document" — we emit an
 * `entity-not-found` error in that case rather than probing further.
 */
export type PropertyEntityLookup = {
  findEntity: (id: string) =>
    | {
        id: string;
        customProperties?: Record<string, string | number | boolean>;
      }
    | undefined;
};

export type PropertyDispatchPlan =
  | {
      kind: "dynamic-param";
      entityId: string;
      paramName: string;
      rawValue: string;
    }
  | {
      kind: "entity-property";
      entityId: string;
      propertyName: StandardProperty;
      /** Canonical store key (e.g. "Linetype Scale"). */
      storeKey: string;
      rawValue: string;
    }
  | {
      kind: "error";
      code: "entity-not-found" | "unknown-property" | "empty-name";
      /** Human-readable message with interpolated id / name. */
      message: string;
      entityId?: string;
      propertyName?: string;
    };

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Build a case-insensitive lookup of StandardProperty values. We create
 * the map eagerly at module init so repeated PROPERTY calls don't
 * re-scan the tuple — matches DYNSET's static `parseValueForType`
 * dispatch pattern.
 */
const STANDARD_PROPERTY_BY_LOWERCASE: Map<string, StandardProperty> = (() => {
  const map = new Map<string, StandardProperty>();
  for (const name of STANDARD_PROPERTIES) {
    map.set(name.toLowerCase(), name);
  }
  return map;
})();

/**
 * Case-insensitive match against the StandardProperty tuple. Returns
 * the canonical-cased name (e.g. `"Layer"`) so downstream dispatches
 * use AutoCAD's exact casing, or null if unknown.
 */
export function canonicalizeStandardProperty(
  name: string,
): StandardProperty | null {
  return STANDARD_PROPERTY_BY_LOWERCASE.get(name.trim().toLowerCase()) ?? null;
}

/**
 * True when the entity's customProperties bag has a key matching `name`
 * exactly (AutoCAD's dynamic-block parameter lookup is case-sensitive —
 * "Length" and "length" are distinct parameters, a constraint baked
 * into the DXF parameter-name round-trip).
 */
function hasCustomProperty(
  entity: { customProperties?: Record<string, string | number | boolean> },
  name: string,
): boolean {
  const bag = entity.customProperties;
  if (!bag) return false;
  return Object.prototype.hasOwnProperty.call(bag, name);
}

// ═══════════════════════════════════════════════════════════════════════════
// Planner
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Decide how to dispatch a PROPERTY command. See the module header for
 * the routing precedence rules. Inputs are pure data; the returned plan
 * instructs the caller which reducer to dispatch into (or which error
 * to surface in the transcript / audit log).
 */
export function planPropertyDispatch(
  target: PropertyTarget,
  lookup: PropertyEntityLookup,
): PropertyDispatchPlan {
  const trimmedName = target.propertyName.trim();
  if (trimmedName.length === 0) {
    return {
      kind: "error",
      code: "empty-name",
      message: "PROPERTY: property name missing.",
    };
  }

  const entity = lookup.findEntity(target.entityId);
  if (!entity) {
    return {
      kind: "error",
      code: "entity-not-found",
      message: `PROPERTY: entity ${target.entityId} not found.`,
      entityId: target.entityId,
    };
  }

  // Routing precedence step 1: dynamic-block INSERT parameter.
  if (hasCustomProperty(entity, trimmedName)) {
    return {
      kind: "dynamic-param",
      entityId: entity.id,
      paramName: trimmedName,
      rawValue: target.rawValue,
    };
  }

  // Routing precedence step 2: case-insensitive StandardProperty match.
  const canonical = canonicalizeStandardProperty(trimmedName);
  if (canonical !== null) {
    return {
      kind: "entity-property",
      entityId: entity.id,
      propertyName: canonical,
      storeKey: STANDARD_PROPERTY_STORE_KEYS[canonical],
      rawValue: target.rawValue,
    };
  }

  // Routing precedence step 3: nothing matched.
  return {
    kind: "error",
    code: "unknown-property",
    message: `PROPERTY: unknown property ${trimmedName}.`,
    entityId: entity.id,
    propertyName: trimmedName,
  };
}
